import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import type { Lead } from '../services/api/leads';
import {
  fetchLeads,
  fetchLead,
  createLead,
  updateLead,
  updateLeadStage,
  reorderLead,
  type ReorderLeadInput,
  fetchProjectRoles,
  createProjectRole,
  updateProjectRole,
  deleteProjectRole,
  addLeadNote,
  deleteLeadNote,
  deleteLead,
  fetchPaymentTerms,
  fetchBillingTypes,
  createPaymentTermApi,
  updatePaymentTermApi,
  deletePaymentTermApi,
  fetchLeadLabels,
  createLeadLabelApi,
  fetchLeadSources,
  createLeadSourceApi,
  fetchLeadLostReasons,
  createLeadLostReasonApi,
  type CreateLeadInput,
  type UpdateLeadInput,
} from '../services/api/leads';

export function useLeads() {
  return useQuery({
    queryKey: ['leads'],
    queryFn: fetchLeads,
  });
}

export function useLead(id: number | null) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => fetchLead(id!),
    enabled: id !== null,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: CreateLeadInput) => createLead(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (err) => onError(err, "Couldn't create lead"),
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateLeadInput }) =>
      updateLead(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
    },
    onError: (err) => onError(err, "Couldn't update lead"),
  });
}

export function useUpdateLeadStage() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, stageId }: { id: number; stageId: number }) =>
      updateLeadStage(id, stageId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
    },
    onError: (err) => onError(err, "Couldn't update lead stage"),
  });
}

type LeadsCache = Awaited<ReturnType<typeof fetchLeads>>;

/**
 * MKS-213 — apply a reorder to the flat lead array exactly the way the
 * server will renumber the column, so the optimistic cache write and the
 * server's eventual response agree. The flat array is sufficient because
 * `LeadKanbanBoard`'s `leadsByStage` memo preserves array order within each
 * stage. If an anchor is not found in the array, returns `leads` unchanged
 * rather than guessing.
 */
export function applyLeadReorder(
  leads: Lead[],
  vars: { id: number; stageId: number; beforeLeadId?: number | null; afterLeadId?: number | null },
): Lead[] {
  const draggedIndex = leads.findIndex((l) => l.id === vars.id);
  if (draggedIndex === -1) return leads;
  const dragged = leads[draggedIndex]!;

  const stageChanged = dragged.stageId !== vars.stageId;
  // MKS-294: a stage change is activity, so the optimistic row carries the
  // new `lastActivityAt` too — otherwise a card dragged out of a stale
  // column would keep its red border until the refetch lands.
  const movedAt = new Date().toISOString();
  // MKS-345 — only `stageId` is known here; the denormalized `stageName` /
  // `stageColor` / `stageKind` on the row stay as they were until the refetch
  // lands. The card does not read them for the moment it is in flight, and
  // `onSettled` invalidates `['leads']` either way.
  const patched: Lead = stageChanged
    ? { ...dragged, stageId: vars.stageId, stageChangedAt: movedAt, lastActivityAt: movedAt }
    : dragged;

  const without = [...leads.slice(0, draggedIndex), ...leads.slice(draggedIndex + 1)];

  let insertAt: number;
  if (vars.beforeLeadId != null) {
    const anchorIndex = without.findIndex((l) => l.id === vars.beforeLeadId);
    if (anchorIndex === -1) return leads;
    insertAt = anchorIndex;
  } else if (vars.afterLeadId != null) {
    const anchorIndex = without.findIndex((l) => l.id === vars.afterLeadId);
    if (anchorIndex === -1) return leads;
    insertAt = anchorIndex + 1;
  } else {
    // No anchor — append immediately after the last lead whose stage matches
    // the target, falling back to the end of the array.
    let lastMatch = -1;
    for (let i = 0; i < without.length; i++) {
      if (without[i]!.stageId === vars.stageId) lastMatch = i;
    }
    insertAt = lastMatch === -1 ? without.length : lastMatch + 1;
  }

  return [...without.slice(0, insertAt), patched, ...without.slice(insertAt)];
}

/**
 * MKS-213 — persist a drag-drop reorder within (or across) a stage column.
 * Optimistic — mirrors `useReorderProjectStates` (projectStates.ts:165):
 * cancel in-flight 'leads' queries, snapshot, write the reordered array back,
 * roll back + toast on error, and invalidate on settle so the authoritative
 * server order always wins eventually.
 */
export function useReorderLead() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<
    { data: Lead },
    Error,
    { id: number } & ReorderLeadInput,
    { previous: LeadsCache | undefined }
  >({
    mutationFn: ({ id, ...input }) => reorderLead(id, input),
    onMutate: async (vars) => {
      const key = ['leads'];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<LeadsCache>(key);
      if (previous) {
        queryClient.setQueryData<LeadsCache>(key, {
          ...previous,
          data: applyLeadReorder(previous.data, vars),
        });
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['leads'], context.previous);
      }
      onError(err, "Couldn't reorder lead");
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', vars.id] });
    },
  });
}

export function useProjectRoles() {
  return useQuery({
    queryKey: ['projectRoles'],
    queryFn: fetchProjectRoles,
  });
}

export function useCreateProjectRole() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ name, code }: { name: string; code?: string }) => createProjectRole(name, code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectRoles'] });
    },
    onError: (err) => onError(err, "Couldn't create role"),
  });
}

export function useUpdateProjectRole() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; name?: string; code?: string | null }) =>
      updateProjectRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectRoles'] });
    },
    onError: (err) => onError(err, "Couldn't update role"),
  });
}

export function useAddLeadNote() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ leadId, content }: { leadId: number; content: string }) =>
      addLeadNote(leadId, content),
    // F-210: scope to the specific lead instead of broadcasting all cached leads.
    // Mirrors the pattern in useUpdateLead (line 53).
    onSuccess: (_data, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    },
    onError: (err) => onError(err, "Couldn't add note"),
  });
}

export function useDeleteLeadNote() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ leadId, noteId }: { leadId: number; noteId: number }) =>
      deleteLeadNote(leadId, noteId),
    // F-210: scope to the specific lead instead of broadcasting all cached leads.
    onSuccess: (_data, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    },
    onError: (err) => onError(err, "Couldn't delete note"),
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteLead(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.removeQueries({ queryKey: ['lead', id] });
    },
    onError: (err) => onError(err, "Couldn't delete lead"),
  });
}

export function usePaymentTerms() {
  return useQuery({
    queryKey: ['paymentTerms'],
    queryFn: fetchPaymentTerms,
  });
}

/**
 * MKS-94 — billing types offerable on a lead. `enabled` lets the lead form skip
 * the request entirely while the release flag is off.
 */
export function useBillingTypes(enabled = true) {
  return useQuery({
    queryKey: ['billingTypes'],
    queryFn: fetchBillingTypes,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useCreatePaymentTerm() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ name, days }: { name: string; days: number }) =>
      createPaymentTermApi(name, days),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentTerms'] });
    },
    onError: (err) => onError(err, "Couldn't create payment term"),
  });
}

export function useUpdatePaymentTerm() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; name?: string; days?: number }) =>
      updatePaymentTermApi(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentTerms'] });
    },
    onError: (err) => onError(err, "Couldn't update payment term"),
  });
}

export function useDeletePaymentTerm() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deletePaymentTermApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentTerms'] });
    },
    onError: (err) => onError(err, "Couldn't delete payment term"),
  });
}

export function useLeadLabels() {
  return useQuery({
    queryKey: ['leadLabels'],
    queryFn: fetchLeadLabels,
  });
}

export function useCreateLeadLabel() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ name, color }: { name: string; color: string }) => createLeadLabelApi(name, color),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadLabels'] });
    },
    onError: (err) => onError(err, "Couldn't create label"),
  });
}

export function useLeadSources() {
  return useQuery({
    queryKey: ['leadSources'],
    queryFn: fetchLeadSources,
  });
}

export function useCreateLeadSource() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (name: string) => createLeadSourceApi(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadSources'] });
    },
    onError: (err) => onError(err, "Couldn't create source"),
  });
}

/**
 * MKS-347 — the configurable "why did we lose it?" list. `enabled` lets the
 * board skip the request entirely while the release flag is off, the same way
 * `useBillingTypes` does.
 */
export function useLeadLostReasons(enabled = true) {
  return useQuery({
    queryKey: ['leadLostReasons'],
    queryFn: fetchLeadLostReasons,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useCreateLeadLostReason() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (name: string) => createLeadLostReasonApi(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadLostReasons'] });
    },
    onError: (err) => onError(err, "Couldn't create lost reason"),
  });
}

export function useDeleteProjectRole() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteProjectRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectRoles'] });
    },
    onError: (err) => onError(err, "Couldn't delete role"),
  });
}
